import { apiFetch } from './http';
import { fetchPage, type ListParams, type PageResponse } from './collections';

/**
 * The platform surface of 07 §4.3 (`AM-134` point 8): tenants,
 * provisioning, the platform side of support access grants, `platform_audit_logs` and feature
 * flags. Every endpoint here runs under `RequirePlatformPermission` (ISO-24) and never carries a
 * business tenant context - only metadata, aggregate counts and the two trails of the boundary.
 *
 * Nulos explícitos (07 §1.15, `AM-134 point 15`): every optional field of a response is declared
 * `campo?: T | null`, and every reader below tolerates `null` and absent alike.
 */

export interface PlatformTenantSummary {
  id: string;
  slug: string;
  name: string;
  status: string;
  timeZone: string;
  defaultCurrency: string;
  activatedAt?: string | null;
  suspendedAt?: string | null;
  deletionRequestedAt?: string | null;
  purgeScheduledAt?: string | null;
  createdAt: string;
}

/** How many, never which (ISO-30, `platform.count_entity`). */
export interface PlatformTenantCounts {
  tickets: number;
  users: number;
  organizations: number;
}

export interface ProvisioningStepEntry {
  key: string;
  order: number;
  status: string;
  attempts: number;
  error?: string | null;
  completedAt?: string | null;
}

/** One support access grant, on either side of the boundary (ISO-25). */
export interface SupportAccessGrantEntry {
  id: string;
  tenantId: string;
  tenantName: string;
  platformAdminId: string;
  platformAdminDisplayName: string;
  reason: string;
  scope: string;
  isEmergency: boolean;
  status: string;
  requestedAt: string;
  approvedByUserId?: string | null;
  approvedByDisplayName?: string | null;
  approvedAt?: string | null;
  expiresAt: string;
  revokedAt?: string | null;
  revokedReason?: string | null;
}

/** The file of one tenant: metadata, saga, aggregate counts and the grants over it. */
export interface PlatformTenantDetail {
  tenant: PlatformTenantSummary;
  counts: PlatformTenantCounts;
  provisioningSteps: ProvisioningStepEntry[];
  supportGrants: SupportAccessGrantEntry[];
}

/** Body of `POST /platform/tenants`: the same fields the provisioning CLI asks for (AM-97). */
export interface CreatePlatformTenantRequest {
  slug: string;
  name: string;
  ownerEmail: string;
  ownerFirstName?: string | null;
  ownerLastName?: string | null;
  timeZone?: string | null;
  defaultCurrency?: string | null;
  defaultLocale?: string | null;
  /** `AM-202`: absent means `true` - a company that bills its own clients, the historical default. */
  commercialBillingEnabled?: boolean | null;
}

/** What `POST /platform/tenants` and the retry answer: the saga's outcome, not the catalog row. */
export interface ProvisioningOutcome {
  tenantId: string;
  slug: string;
  status: string;
  steps: ProvisioningStepEntry[];
  /** The alta found its slug taken and continued that tenant's saga; a retry answers `false`. */
  reopened: boolean;
}

/** A platform administrator as the audit's filter names one (`GET /platform/admins`). */
export interface PlatformAdminEntry {
  id: string;
  displayName: string;
  isActive: boolean;
}

/** Body of `POST /platform/support-access-grants` (ISO-25.1, DA-06). */
export interface RequestSupportAccessInput {
  tenantId: string;
  reason: string;
  scope?: string;
  durationHours?: number;
  isEmergency: boolean;
}

/** One row of `platform_audit_logs` (07 §4.3), with the names the identifiers stand for. */
export interface PlatformAuditLogEntry {
  id: string;
  platformAdminId?: string | null;
  platformAdminDisplayName?: string | null;
  tenantId?: string | null;
  tenantName?: string | null;
  action: string;
  targetType?: string | null;
  targetId?: string | null;
  changes?: string | null;
  summary?: string | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  correlationId?: string | null;
  createdAt: string;
}

/** A flag as the platform defines it. */
export interface FeatureFlagEntry {
  key: string;
  description: string;
  defaultEnabled: boolean;
}

/** A flag seen from one tenant's file, on the platform side: the definition plus its override. */
export interface TenantFeatureFlagEntry {
  key: string;
  description: string;
  isEnabled: boolean;
  isOverridden: boolean;
}

/**
 * Whose days a holiday reference set holds (`AM-362`, `ck_holiday_reference_sets_source`): the
 * hand-written list the platform ships with, or one of the two public sources the worker downloads
 * every month.
 */
export const HOLIDAY_REFERENCE_SOURCES = ['seed', 'argentinadatos', 'nager_date'] as const;
export type HolidayReferenceSource = (typeof HOLIDAY_REFERENCE_SOURCES)[number];

/** One reference set of `GET /platform/holiday-references`. */
export interface HolidayReferenceSetSummary {
  year: number;
  source: string;
  days: number;
  /** When its days came from that source; `null` for the platform's own list. */
  fetchedAt?: string | null;
  /** The last time the monthly pass found the same days. */
  checkedAt?: string | null;
}

/**
 * `GET /platform/holiday-references` (`AM-362`, `platform.health.view`): the sets of the country the
 * tenants import from, from the current year on, and which of the current and the next year have no
 * reference day.
 */
export interface HolidayReferenceStatus {
  countryCode: string;
  currentYear: number;
  missingYears: number[];
  lastCheckedAt?: string | null;
  sets: HolidayReferenceSetSummary[];
}

/** The eleven keys of `platform_admins.permissions` (08 §6.2, `AM-134` point 1), for reference. */
export const PLATFORM_PERMISSIONS = {
  tenantsView: 'platform.tenants.view',
  tenantsManage: 'platform.tenants.manage',
  plansManage: 'platform.plans.manage',
  subscriptionsManage: 'platform.subscriptions.manage',
  featureFlagsManage: 'platform.feature_flags.manage',
  healthView: 'platform.health.view',
  usageView: 'platform.usage.view',
  auditView: 'platform.audit.view',
  supportAccessRequest: 'platform.support_access.request',
  supportAccessEmergency: 'platform.support_access.emergency',
  jobsOperate: 'platform.jobs.operate',
} as const;

/** One key of `PLATFORM_PERMISSIONS` (08 §6.2): what `session.platform.permissions` holds. */
export type PlatformPermissionKey =
  (typeof PLATFORM_PERMISSIONS)[keyof typeof PLATFORM_PERMISSIONS];

/** The four states a grant is derived into (`AM-134` point 6). */
export const SUPPORT_ACCESS_STATUSES = ['pending', 'active', 'expired', 'revoked'] as const;
export type SupportAccessStatus = (typeof SUPPORT_ACCESS_STATUSES)[number];

/** `read_only` is the default scope of a grant; `full` the alternative (`AM-134` point 3). */
export const SUPPORT_ACCESS_SCOPES = ['read_only', 'full'] as const;

/** The closed vocabulary of `platform_audit_logs.action` (`AM-134` point 1), for the audit filter. */
export const PLATFORM_AUDIT_ACTIONS = [
  'tenant.create',
  'tenant.provisioning_retry',
  'tenant.suspend',
  'tenant.reactivate',
  'tenant.deletion_request',
  'tenant.deletion_cancel',
  'tenant.time_zone_change',
  'feature_flag.define',
  'feature_flag.set',
  'support_access.request',
  'support_access.emergency',
  'support_access.approve',
  'support_access.reject',
  'support_access.revoke',
  'support_access.session_open',
  'support_access.session_close',
  'support_access.action',
  'system.support_access_expired',
  'system.holiday_reference_updated',
] as const;
export type PlatformAuditAction = (typeof PLATFORM_AUDIT_ACTIONS)[number];

export const platformApi = {
  tenants: {
    list: (): Promise<PlatformTenantSummary[]> =>
      apiFetch<PlatformTenantSummary[]>('/api/v1/platform/tenants'),
    get: (id: string): Promise<PlatformTenantDetail> =>
      apiFetch<PlatformTenantDetail>(`/api/v1/platform/tenants/${id}`),
    create: (body: CreatePlatformTenantRequest): Promise<ProvisioningOutcome> =>
      apiFetch<ProvisioningOutcome>('/api/v1/platform/tenants', { method: 'POST', body }),
    steps: (id: string): Promise<ProvisioningStepEntry[]> =>
      apiFetch<ProvisioningStepEntry[]>(`/api/v1/platform/tenants/${id}/provisioning-steps`),
    retryProvisioning: (id: string): Promise<ProvisioningOutcome> =>
      apiFetch<ProvisioningOutcome>(`/api/v1/platform/tenants/${id}/provisioning/retry`, {
        method: 'POST',
      }),
    suspend: (id: string): Promise<PlatformTenantSummary> =>
      apiFetch<PlatformTenantSummary>(`/api/v1/platform/tenants/${id}/suspend`, {
        method: 'POST',
      }),
    reactivate: (id: string): Promise<PlatformTenantSummary> =>
      apiFetch<PlatformTenantSummary>(`/api/v1/platform/tenants/${id}/reactivate`, {
        method: 'POST',
      }),
    requestDeletion: (id: string): Promise<PlatformTenantSummary> =>
      apiFetch<PlatformTenantSummary>(`/api/v1/platform/tenants/${id}/deletion-request`, {
        method: 'POST',
      }),
    cancelDeletion: (id: string): Promise<PlatformTenantSummary> =>
      apiFetch<PlatformTenantSummary>(`/api/v1/platform/tenants/${id}/cancel-deletion`, {
        method: 'POST',
      }),
    /** `PUT /platform/tenants/{id}/time-zone` (`AM-253`): `422 invalid_time_zone` on `timeZone` for a zone the platform does not know. */
    changeTimeZone: (id: string, timeZone: string): Promise<PlatformTenantSummary> =>
      apiFetch<PlatformTenantSummary>(`/api/v1/platform/tenants/${id}/time-zone`, {
        method: 'PUT',
        body: { timeZone },
      }),
  },

  grants: {
    /** `GET /platform/support-access-grants` (filters `tenantId`, `status`). */
    list: (
      filters: { tenantId?: string; status?: string } = {},
    ): Promise<SupportAccessGrantEntry[]> => {
      const query = new URLSearchParams();
      if (filters.tenantId !== undefined) {
        query.set('tenantId', filters.tenantId);
      }
      if (filters.status !== undefined) {
        query.set('status', filters.status);
      }
      const text = query.toString();
      return apiFetch<SupportAccessGrantEntry[]>(
        `/api/v1/platform/support-access-grants${text.length === 0 ? '' : `?${text}`}`,
      );
    },
    request: (input: RequestSupportAccessInput): Promise<SupportAccessGrantEntry> =>
      apiFetch<SupportAccessGrantEntry>('/api/v1/platform/support-access-grants', {
        method: 'POST',
        body: {
          tenantId: input.tenantId,
          reason: input.reason,
          scope: input.scope,
          durationHours: input.durationHours,
          isEmergency: input.isEmergency,
        },
      }),
    revoke: (id: string, reason?: string): Promise<SupportAccessGrantEntry> =>
      apiFetch<SupportAccessGrantEntry>(`/api/v1/platform/support-access-grants/${id}/revoke`, {
        method: 'POST',
        body: { reason: reason === undefined || reason.trim() === '' ? null : reason },
      }),
  },

  auditLogs: {
    /** `GET /platform/audit-logs` (`platform.audit.view`): cursor, filters `platformAdminId`/`tenantId`/`action`/`createdAt[from|to]`. */
    list: (
      params: ListParams,
      signal?: AbortSignal,
    ): Promise<PageResponse<PlatformAuditLogEntry>> =>
      fetchPage<PlatformAuditLogEntry>('/api/v1/platform/audit-logs', params, signal),
  },

  holidayReferences: {
    /** `GET /platform/holiday-references` (`platform.health.view`, `AM-362`). */
    get: (signal?: AbortSignal): Promise<HolidayReferenceStatus> =>
      apiFetch<HolidayReferenceStatus>('/api/v1/platform/holiday-references', { signal }),
  },

  admins: {
    /** `GET /platform/admins` (`platform.audit.view`): every administrator the trail may name, for its filter. */
    list: (): Promise<PlatformAdminEntry[]> =>
      apiFetch<PlatformAdminEntry[]>('/api/v1/platform/admins'),
  },

  featureFlags: {
    list: (): Promise<FeatureFlagEntry[]> =>
      apiFetch<FeatureFlagEntry[]>('/api/v1/platform/feature-flags'),
    define: (
      key: string,
      description: string | null,
      defaultEnabled: boolean,
    ): Promise<FeatureFlagEntry> =>
      apiFetch<FeatureFlagEntry>(`/api/v1/platform/feature-flags/${key}`, {
        method: 'PUT',
        body: { description, defaultEnabled },
      }),
    listForTenant: (tenantId: string): Promise<TenantFeatureFlagEntry[]> =>
      apiFetch<TenantFeatureFlagEntry[]>(`/api/v1/platform/tenants/${tenantId}/feature-flags`),
    setForTenant: (
      tenantId: string,
      key: string,
      isEnabled: boolean,
    ): Promise<TenantFeatureFlagEntry> =>
      apiFetch<TenantFeatureFlagEntry>(
        `/api/v1/platform/tenants/${tenantId}/feature-flags/${key}`,
        { method: 'PUT', body: { isEnabled } },
      ),
  },
};
